import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  IndianRupee, Search, Calendar, ArrowUpRight, ArrowDownLeft,
  Plus, Filter, RefreshCw, Loader2, Wallet, Download,
  CreditCard, TrendingUp, TrendingDown, Clock
} from 'lucide-react';
import { initiatePayUPayment } from '../lib/payu';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VendorWalletTab() {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ type: 'all', search: '', dateFrom: '', dateTo: '' });
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNotes, setTopupNotes] = useState('');
  const [topupTransactionId, setTopupTransactionId] = useState('');
  const [topupScreenshot, setTopupScreenshot] = useState(null);
  const [topupRequests, setTopupRequests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [adminPaymentInfo, setAdminPaymentInfo] = useState([]);
  const [payuLoading, setPayuLoading] = useState(false);
  const getHeaders = () => {
    const t = localStorage.getItem('vendor_token');
    return { Authorization: `Bearer ${t}` };
  };

  const handlePayUTopup = async () => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setPayuLoading(true);
    try {
      await initiatePayUPayment({ token: localStorage.getItem('vendor_token'), purpose: 'vendor_wallet', amount: topupAmount });
    } catch (err) {
      toast.error(err.message || 'Failed to start PayU payment');
      setPayuLoading(false);
    }
  };

  const fetchWallet = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-wallet/balance`, { headers: getHeaders() });
      setWallet(res.data);
    } catch (err) {
      console.error('Error fetching wallet:', err);
    }
  }, []);

  const [txnFilteredSummary, setTxnFilteredSummary] = useState(null);
  
  const fetchTransactions = useCallback(async (p = 1) => {
    setTxnLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.search) params.search = filters.search;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      const res = await axios.get(`${API}/api/vendor-wallet/transactions`, { headers: getHeaders(), params });
      setTransactions(res.data.transactions || []);
      setTotalPages(res.data.pages || 1);
      setTxnFilteredSummary(res.data.filtered_summary || null);
      setPage(p);
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setTxnLoading(false);
    }
  }, [filters]);

  const fetchTopupRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-wallet/topup/my-requests`, { headers: getHeaders() });
      setTopupRequests(res.data.requests || []);
    } catch (err) { console.error('Failed to fetch topup requests:', err); }
  }, []);

  const fetchAdminPaymentInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/platform-config/admin-payment-info`);
      setAdminPaymentInfo(res.data.payment_info || []);
    } catch (err) { console.error('Failed to fetch payment info:', err); }
  }, []);

  useEffect(() => {
    Promise.all([fetchWallet(), fetchTransactions(1), fetchTopupRequests(), fetchAdminPaymentInfo()]).finally(() => setLoading(false));
  }, [fetchWallet, fetchTransactions, fetchTopupRequests, fetchAdminPaymentInfo]);

  useEffect(() => {
    fetchTransactions(1);
  }, [filters, fetchTransactions]);

  const handleTopupRequest = async () => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', topupAmount);
      formData.append('notes', topupNotes);
      formData.append('utr_number', topupTransactionId);
      if (topupScreenshot) {
        formData.append('screenshot', topupScreenshot);
      }
      await axios.post(`${API}/api/vendor-wallet/topup/request`, formData, { headers: getHeaders() });
      toast.success('Top-up request submitted to admin');
      setShowTopupDialog(false);
      setTopupAmount('');
      setTopupNotes('');
      setTopupTransactionId('');
      setTopupScreenshot(null);
      fetchTopupRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'earning': return <ArrowDownLeft className="w-4 h-4 text-green-500" />;
      case 'redemption': return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'topup': return <Plus className="w-4 h-4 text-blue-500" />;
      case 'b2b_purchase': return <ArrowUpRight className="w-4 h-4 text-orange-500" />;
      case 'walkin_commission_deduction': return <ArrowUpRight className="w-4 h-4 text-amber-500" />;
      case 'patient_order_referral_deduction': return <ArrowUpRight className="w-4 h-4 text-purple-500" />;
      default: return <IndianRupee className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTypeBadge = (type) => {
    const map = {
      earning: { label: 'Earning', className: 'bg-green-100 text-green-700' },
      redemption: { label: 'Redeemed', className: 'bg-red-100 text-red-700' },
      topup: { label: 'Top-up', className: 'bg-blue-100 text-blue-700' },
      b2b_purchase: { label: 'B2B Purchase', className: 'bg-orange-100 text-orange-700' },
      walkin_commission_deduction: { label: 'Walk-in Referral Charges', className: 'bg-amber-100 text-amber-700' },
      patient_order_referral_deduction: { label: 'Patient Order Referral Charges', className: 'bg-purple-100 text-purple-700' }
    };
    const m = map[type] || { label: type, className: 'bg-slate-100 text-slate-600' };
    return <Badge className={m.className}>{m.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vendor-wallet-tab">
      {/* Wallet Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-teal-600" />
              <span className="text-xs text-teal-600 font-medium">Balance</span>
            </div>
            <p className="text-xl md:text-2xl font-bold text-teal-700" data-testid="vendor-wallet-balance">
              ₹{(wallet?.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-xs text-slate-500">Total Earned</span>
            </div>
            <p className="text-lg md:text-xl font-bold">₹{(wallet?.total_earned || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <span className="text-xs text-slate-500">Total Redeemed</span>
            </div>
            <p className="text-lg md:text-xl font-bold">₹{(wallet?.total_redeemed || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-slate-500">Top-ups</span>
            </div>
            <p className="text-lg md:text-xl font-bold">₹{(wallet?.total_topups || 0).toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Admin Payment Info - Always Visible */}
      {adminPaymentInfo.length > 0 && (
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white" data-testid="admin-payment-info-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-blue-700 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Admin Payment Details (for Top-up)
            </CardTitle>
            <CardDescription className="text-blue-600 text-xs">Make payment to this account and then submit a top-up request with proof</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-start gap-4">
              {adminPaymentInfo.map((info, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-4">
                  {info.qr_image_url && (
                    <img 
                      src={info.qr_image_url} 
                      alt="Admin QR Code" 
                      className="w-32 h-32 border-2 border-blue-300 rounded-lg object-contain bg-white p-1" 
                      data-testid="admin-qr-code-img"
                    />
                  )}
                  <div className="space-y-1">
                    {info.upi_id && (
                      <div>
                        <span className="text-xs text-blue-600 font-medium">UPI ID: </span>
                        <span className="text-sm font-bold text-blue-800 bg-blue-100 px-2 py-0.5 rounded select-all" data-testid="admin-upi-id">{info.upi_id}</span>
                      </div>
                    )}
                    {info.payee_name && (
                      <div>
                        <span className="text-xs text-blue-600 font-medium">Payee: </span>
                        <span className="text-sm text-blue-800">{info.payee_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => setShowTopupDialog(true)} data-testid="vendor-topup-btn">
          <Plus className="w-4 h-4 mr-1" /> Request Top-up
        </Button>
        <Button size="sm" variant="outline" onClick={() => { fetchWallet(); fetchTransactions(page); }}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" /> Transaction History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="relative col-span-2 md:col-span-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="pl-9 text-sm"
                data-testid="wallet-txn-search"
              />
            </div>
            <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
              <SelectTrigger data-testid="wallet-txn-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="earning">Earnings</SelectItem>
                <SelectItem value="topup">Top-ups</SelectItem>
                <SelectItem value="redemption">Redeemed</SelectItem>
                <SelectItem value="b2b_purchase">B2B Purchases</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
              className="text-sm"
              data-testid="wallet-txn-date-from"
            />
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))}
              className="text-sm"
              data-testid="wallet-txn-date-to"
            />
          </div>

          {/* Filtered Summary */}
          {txnFilteredSummary && txnFilteredSummary.count > 0 && (filters.type !== 'all' || filters.search || filters.dateFrom || filters.dateTo) && (
            <div className="p-2.5 bg-violet-50 border border-violet-200 rounded-lg flex flex-wrap items-center gap-4 text-xs">
              <span className="font-medium text-violet-700">{txnFilteredSummary.count} transactions</span>
              <span className="text-slate-400">|</span>
              <span>Credits: <strong className="text-green-600">₹{(txnFilteredSummary.credits || 0).toLocaleString()}</strong></span>
              <span className="text-slate-400">|</span>
              <span>Debits: <strong className="text-red-600">₹{Math.abs(txnFilteredSummary.debits || 0).toLocaleString()}</strong></span>
              <span className="text-slate-400">|</span>
              <span>Net: <strong className="text-violet-700">₹{(txnFilteredSummary.total_amount || 0).toLocaleString()}</strong></span>
            </div>
          )}

          {/* Transactions List */}
          {txnLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No transactions found</div>
          ) : (
            <div className="space-y-2">
              {transactions.filter(t => t.type !== 'walkin_commission_deduction' && t.type !== 'patient_order_referral_deduction').map((txn) => (
                <div key={txn.id} className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-slate-50 transition" data-testid="wallet-txn-row">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      {getTypeIcon(txn.type)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{txn.description}</p>
                      <p className="text-xs text-slate-400">
                        {txn.created_at ? new Date(txn.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-bold ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {txn.amount >= 0 ? '+' : ''}₹{Math.abs(txn.amount).toLocaleString('en-IN')}
                    </p>
                    {getTypeBadge(txn.type)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => fetchTransactions(page - 1)}>Prev</Button>
              <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => fetchTransactions(page + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Top-up Requests */}
      {topupRequests.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top-up Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {topupRequests.map((req) => (
                <div key={req.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid="topup-request-row">
                  <div>
                    <p className="text-sm font-medium">₹{req.amount?.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-slate-400">{req.created_at ? new Date(req.created_at).toLocaleDateString('en-IN') : ''}</p>
                    {req.notes && <p className="text-xs text-slate-500 mt-1">{req.notes}</p>}
                  </div>
                  <Badge className={
                    req.status === 'approved' ? 'bg-green-100 text-green-700' :
                    req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }>{req.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top-up Dialog */}
      <Dialog open={showTopupDialog} onOpenChange={setShowTopupDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Wallet Top-up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Admin Payment Info */}
            {adminPaymentInfo.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-700 mb-2">Pay to Admin Account:</p>
                {adminPaymentInfo.map((info, idx) => (
                  <div key={idx} className="space-y-2">
                    {info.upi_id && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-blue-600 font-medium">UPI ID:</span>
                        <span className="text-sm font-bold text-blue-800 bg-blue-100 px-2 py-0.5 rounded select-all">{info.upi_id}</span>
                      </div>
                    )}
                    {info.payee_name && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-blue-600 font-medium">Payee:</span>
                        <span className="text-sm text-blue-800">{info.payee_name}</span>
                      </div>
                    )}
                    {info.qr_image_url && (
                      <div className="flex justify-center mt-2">
                        <img src={info.qr_image_url} alt="Admin QR Code" className="w-40 h-40 border-2 border-blue-300 rounded-lg object-contain bg-white p-1" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div>
              <Label>Amount (₹) *</Label>
              <Input type="number" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} placeholder="Enter amount" data-testid="topup-amount-input" />
              <Button
                type="button"
                variant="outline"
                className="w-full mt-2"
                onClick={handlePayUTopup}
                disabled={payuLoading}
                data-testid="payu-instant-recharge-btn"
              >
                {payuLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Pay Instantly via PayU
              </Button>
              <p className="text-xs text-slate-400 mt-1 text-center">OR submit manual UTR details below</p>
            </div>
            <div>
              <Label>Transaction ID / UTR Number</Label>
              <Input value={topupTransactionId} onChange={(e) => setTopupTransactionId(e.target.value)} placeholder="e.g. UTR123456789" data-testid="topup-transaction-id-input" />
            </div>
            <div>
              <Label>Payment Screenshot</Label>
              <Input type="file" accept="image/*" onChange={(e) => setTopupScreenshot(e.target.files[0])} data-testid="topup-screenshot-input" />
              {topupScreenshot && <p className="text-xs text-green-600 mt-1">File selected: {topupScreenshot.name}</p>}
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={topupNotes} onChange={(e) => setTopupNotes(e.target.value)} placeholder="Any additional details" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTopupDialog(false)}>Cancel</Button>
            <Button onClick={handleTopupRequest} disabled={submitting} data-testid="submit-topup-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
